import { useEffect, useRef } from 'react';
import { useAssistantChat } from './useAssistantChat';
import type { KianEntityContext } from '../types/kian';

/**
 * Synchronisiert Assistant-Session mit dem aktuellen Seitenkontext (wenn nicht angeheftet).
 */
export function useKianSession(
  enabled: boolean,
  flyoutEnabled: boolean,
  panelClosed: boolean,
  followPageContext: boolean,
  current: KianEntityContext | null
): void {
  const createSession = useAssistantChat((s) => s.createSession);
  const loadSession = useAssistantChat((s) => s.loadSession);

  const type = current?.type ?? null;
  const id = current?.id ?? null;
  const labelRef = useRef<string | null>(null);
  labelRef.current = current?.label ?? null;
  // Monotone Version, damit spät eintreffende Antworten einer früheren Kontext-Wahl
  // nicht die Session eines neueren Kontextes überschreiben.
  const requestVersion = useRef(0);

  useEffect(() => {
    if (!enabled || !flyoutEnabled || panelClosed || !followPageContext) return;
    const myVersion = ++requestVersion.current;
    const isStale = () => requestVersion.current !== myVersion;

    const targetScratch = !type && !id;
    const { currentSession, currentSessionId, clearActiveSession } = useAssistantChat.getState();
    const alreadyLoaded =
      Boolean(currentSessionId && currentSession) &&
      (targetScratch
        ? !currentSession?.entityType &&
          !currentSession?.entityId &&
          !currentSession?.submissionId
        : currentSession?.entityType === type && currentSession?.entityId === id);
    if (alreadyLoaded) return;
    clearActiveSession();

    void (async () => {
      try {
        const newId =
          !type || !id
            ? await createSession({})
            : await createSession({
                entityType: type,
                entityId: id,
                entityLabel: labelRef.current ?? undefined,
              });
        if (isStale()) return;
        // Falls createSession eine Session zurückgibt, die bereits im Store geladen ist,
        // sparen wir uns den GET-Roundtrip.
        const loaded = useAssistantChat.getState().currentSession;
        if (loaded && loaded.id === newId) return;
        await loadSession(newId);
        if (isStale()) {
          // Spät eingetroffene Antwort: dürfte die neue Kontext-Session nicht überschreiben.
          useAssistantChat.getState().clearActiveSession();
        }
      } catch (e) {
        if (!isStale()) console.error('[useKianSession]', e);
      }
    })();
    return () => {
      // Invalidiere alle laufenden Anfragen dieses Effect-Runs.
      requestVersion.current++;
    };
  }, [enabled, flyoutEnabled, panelClosed, followPageContext, type, id, createSession, loadSession]);
}
